import { useEffect, useRef, useState } from 'react'

export const useMouseTracking = (isLoading = false) => {
	const cardRef = useRef<HTMLDivElement>(null)
	const [background, setBackground] = useState(
		'radial-gradient(82% 100.15% at 0% 5%, rgba(217, 217, 217, 0.10) 0.01%, rgba(217, 217, 217, 0.10) 0.02%, rgba(217, 217, 217, 0.00) 100%)'
	)
	const animationRef = useRef<number | null>(null)
	const [position, setPosition] = useState({ x: 0, y: 0 })

	useEffect(() => {
		const card = cardRef.current
		if (!card || isLoading) return

		const handleMouseMove = (e: MouseEvent) => {
			const rect = card.getBoundingClientRect()
			const x = ((e.clientX - rect.left) / rect.width) * 100
			const y = ((e.clientY - rect.top) / rect.height) * 100

			setBackground(
				`radial-gradient(circle at ${x}% ${y}%, rgba(217, 217, 217, 0.10) 0.01%, rgba(217, 217, 217, 0.10) 0.02%, rgba(217, 217, 217, 0.00) 100%)`
			)
		}

		card.addEventListener('mousemove', handleMouseMove)
		return () => card.removeEventListener('mousemove', handleMouseMove)
	}, [isLoading])

	// Handle loading animation
	useEffect(() => {
		if (isLoading) {
			// Start the animation from top-left (negative position)
			setPosition({ x: -100, y: -100 })

			// Start the animation
			const animate = () => {
				setPosition(prevPosition => {
					// Calculate new position (diagonal movement)
					const newX = prevPosition.x >= 100 ? -200 : prevPosition.x + 4
					const newY = prevPosition.y >= 100 ? -200 : prevPosition.y + 4

					// Update the background with the new position
					setBackground(
						`radial-gradient(circle at ${newX}% ${newY}%, rgba(217, 217, 217, 0.10) 0.01%, rgba(217, 217, 217, 0.10) 0.02%, rgba(217, 217, 217, 0.00) 100%)`
					)

					return { x: newX, y: newY }
				})

				// Continue the animation
				animationRef.current = requestAnimationFrame(animate)
			}

			// Start the animation
			animationRef.current = requestAnimationFrame(animate)
		} else {
			// Reset to default background when not loading
			setPosition({ x: 0, y: 0 })
			setBackground(
				'radial-gradient(82% 100.15% at 0% 5%, rgba(217, 217, 217, 0.10) 0.01%, rgba(217, 217, 217, 0.10) 0.02%, rgba(217, 217, 217, 0.00) 100%)'
			)
		}

		// Clean up animation on unmount or when loading state changes
		return () => {
			if (animationRef.current) {
				cancelAnimationFrame(animationRef.current)
			}
		}
	}, [isLoading])

	return { cardRef, background }
}
